import React, { useEffect, useState } from 'react';
import Input from '../../ui/input';
import Label from '../../ui/label';
import Select from '../../ui/select';
import { getSites } from '../../../redux/api/siteAPI';
import { validateUser } from '../../../model/UserModel';
import { userAdd } from '../../../redux/api/userAPI';
import Button from '../../ui/button';
import { useTranslation } from 'react-i18next';

const sites = [
  { Site_ID: 1, Name: 'yard 1' },
  { Site_ID: 2, Name: 'Room 2' },
  { Site_ID: 3, Name: 'Hallway 1' },
];

const INITIAL_FORM = {
  First_name: '',
  Last_name: '',
  Position: '',
  Phone: '',
  Email: '',
  Role: '',
  Username: '',
  Password: '',
  CPassword: '',
  Notifications: false,
  Email_Notifications: false,
  SMS_Notifications: false,
  Slack_Notifications: false,
  Telegram_Notifications: false,
  Telegram_chat_id: '',
  Language: 'English',
  Site_ID: '',
};

const AddUserModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [errorAdd, setErrorAdd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sitesList, setSitesList] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const sitesRes = await getSites();
        setSitesList(sitesRes.data?.sites || sites);
      } catch (err) {
        console.error('Error fetching sites:', err);
        setSitesList(sites);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchInitialData();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let transformedValue = value;

    if (type === 'checkbox') {
      transformedValue = checked;
      if (name === 'Notifications' && !checked) {
        setFormData((prev) => ({
          ...prev,
          Notifications: false,
          Email_Notifications: false,
          SMS_Notifications: false,
          Slack_Notifications: false,
          Telegram_Notifications: false,
        }));
        return;
      }
    } else if (name === 'First_name' || name === 'Last_name' || name === 'Position') {
      transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
    } else if (name === 'Phone') {
      transformedValue = value.replace(/\D/g, '').slice(0, 8);
    }

    setFormData((prev) => ({ ...prev, [name]: transformedValue }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = async () => {
    // FIX 1: Pre-validate email format before passing to Yup.
    // The blockedEmail custom test does value.split('@')[1].split('.') which
    // throws if the email has no '@', crashing the Yup chain silently.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.Email && !emailRegex.test(formData.Email)) {
      setFormErrors((prev) => ({ ...prev, Email: 'Email is not valid' }));
      return false;
    }

    try {
      const dataToValidate = {
        ...formData,
        Site_ID: formData.Site_ID !== '' ? String(formData.Site_ID) : '',
      };

      await validateUser(dataToValidate);
      setFormErrors({});
      return true;
    } catch (error) {
      if (error.inner && Array.isArray(error.inner) && error.inner.length > 0) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          if (err.path) validationErrors[err.path] = err.message;
        });
        setFormErrors(validationErrors);
      } else if (error.path && error.message) {
        setFormErrors({ [error.path]: error.message });
      } else {
        setErrorAdd(error.message || 'Validation failed');
      }
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorAdd(null);

    const isValid = await validateForm();
    if (!isValid) return;

    setLoading(true);
    try {
      const result = await userAdd(formData);

      if (result.error) {
        // FIX 2: Extract the detail string directly — don't pass through t()
        // interpolation which can silently fail if the key/template is wrong.
        const detail =
          result.error?.response?.data?.detail ||
          result.error?.response?.data?.message ||
          result.error?.message ||
          'Error creating user';
        setErrorAdd(detail);
        return;
      }

      closeModal(true);
    } catch (error) {
      setErrorAdd(error?.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = (refresh = false) => {
    setFormData(INITIAL_FORM);
    setFormErrors({});
    setErrorAdd(null);
    onClose(refresh);
  };

  const generateRandomPassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const specials = '!@#$%^&*';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';

    let password =
      specials[Math.floor(Math.random() * specials.length)] +
      uppers[Math.floor(Math.random() * uppers.length)] +
      lowers[Math.floor(Math.random() * lowers.length)] +
      digits[Math.floor(Math.random() * digits.length)];

    for (let i = 0; i < 4; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    password = password.split('').sort(() => Math.random() - 0.5).join('');
    setFormData((prev) => ({ ...prev, Password: password, CPassword: password }));
  };

  const isRTL = formData.Language === 'ar';

  return (
    <form data-testid="user-add-form" onSubmit={handleSubmit} noValidate>
      <div className="min-w-[350px] overflow-y-auto grid gap-6 max-md:grid-cols-1 grid-cols-2 w-auto m-2 bg-white max-md:p-3 p-8 rounded-3xl shadow-lg">
        <p className="flex justify-center col-span-full text-xl text-[#4C4C66] font-bold mb-4">
          {t('add_user_details')}
        </p>

        <Input
          required
          label={t('first_name')}
          name="First_name"
          value={formData.First_name}
          onChange={handleChange}
          placeholder={t('enter_first_name')}
          error={formErrors.First_name}
        />
        <Input
          required
          label={t('last_name')}
          name="Last_name"
          value={formData.Last_name}
          onChange={handleChange}
          placeholder={t('enter_last_name')}
          error={formErrors.Last_name}
        />
        <Input
          required
          label={t('email')}
          name="Email"
          value={formData.Email}
          onChange={handleChange}
          placeholder={t('enter_email')}
          error={formErrors.Email}
        />
        <Input
          required
          label={t('phone')}
          name="Phone"
          phone
          value={formData.Phone}
          onChange={handleChange}
          placeholder={t('enter_phone')}
          error={formErrors.Phone}
          maxLength={8}
        />
        <div className="col-span-full">
          <Input
            required
            label={t('position')}
            name="Position"
            value={formData.Position}
            onChange={handleChange}
            placeholder={t('enter_position')}
            error={formErrors.Position}
          />
        </div>

        <Select
          inputId="e2e-user-role"
          required
          selectOnly
          options={[
            { label: t('admin'), value: 'admin' },
            { label: t('analyst'), value: 'analyst' },
            // { label: t('user'), value: "user" },
          ]}
          label={t('role')}
          isSearchable={false}
          name="Role"
          value={formData.Role ? { label: t(formData.Role), value: formData.Role } : null}
          onChange={(option) => setFormData((prev) => ({ ...prev, Role: option.value }))}
          placeholder={t('select_role')}
          error={formErrors.Role}
        />
        <Select
          inputId="e2e-user-site"
          options={sitesList.map((site) => ({ label: site.Name, value: site.Site_ID }))}
          label={t('site')}
          name="Site_ID"
          value={
            formData.Site_ID
              ? {
                  label: sitesList.find((s) => s.Site_ID === formData.Site_ID)?.Name || '',
                  value: formData.Site_ID,
                }
              : null
          }
          onChange={(option) => setFormData((prev) => ({ ...prev, Site_ID: option.value }))}
          placeholder={t('select_site')}
          error={formErrors.Site_ID}
        />

        <Input
          required
          type="password"
          label={t('password')}
          name="Password"
          value={formData.Password}
          onChange={handleChange}
          placeholder={t('enter_password')}
          error={formErrors.Password}
        />
        <Input
          required
          type="password"
          label={t('confirm_password')}
          name="CPassword"
          value={formData.CPassword}
          onChange={handleChange}
          placeholder={t('confirm_password')}
          error={formErrors.CPassword}
        />

        <div className="col-span-full">
          <p className="text-lg text-[#4C4C66] font-semibold mb-4">{t('notification_settings')}</p>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center justify-between py-2`}>
              <div className="text-gray-800 font-medium">
                <Label setValue={t('notifications')} />
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  name="Notifications"
                  type="checkbox"
                  checked={!!formData.Notifications}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0071BC]" />
              </label>
            </div>

            {formData.Notifications && (
              <div className="mt-3 space-y-2 pl-4 border-l-2 border-gray-300">
                {/* <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center justify-between py-2`}>
                  <div className="text-gray-700">
                    <Label setValue={t('email_notifications')} />
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      name="Email_Notifications"
                      type="checkbox"
                      checked={!!formData.Email_Notifications}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0071BC]" />
                  </label>
                </div> */}
                {[
                  ['Email_Notifications', 'Email Notifications'],
                  ['Slack_Notifications', 'Slack Notifications'],
                  ['SMS_Notifications', 'SMS Notifications'],
                  ['Telegram_Notifications', 'Telegram Notifications'],
                ].map(([name, label]) => (
                  <div key={name} className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center justify-between py-2`}>
                    <div className="text-gray-700">{label}</div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        name={name}
                        type="checkbox"
                        checked={!!formData[name]}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0071BC]" />
                    </label>
                  </div>
                ))}
                <Input
                  label="Telegram chat ID"
                  name="Telegram_chat_id"
                  value={formData.Telegram_chat_id}
                  onChange={handleChange}
                  placeholder="Optional; company default is used when blank"
                />
              </div>
            )}
          </div>
        </div>

        <div className="col-span-full flex justify-center mt-4">
          <Button type="button" onClick={generateRandomPassword} size="large" color="secondary">
            {t('generate_password')}
          </Button>
        </div>

        {Object.keys(formErrors).length > 0 && (
          <div className="col-span-full">
            <ul className="text-xs text-[#FF0000] space-y-1 list-disc list-inside">
              {Object.entries(formErrors).map(([field, msg]) =>
                msg ? <li key={field}>{msg}</li> : null
              )}
            </ul>
          </div>
        )}

        {/* FIX 2: Render errorAdd directly — no t() interpolation that can silently fail */}
        {errorAdd && (
          <div className="col-span-full flex justify-center">
            <span className="my-4 text-xs text-[#FF0000]">{errorAdd}</span>
          </div>
        )}

        <div className="col-span-full flex justify-center mt-10 space-x-5">
          <Button type="button" onClick={() => closeModal(false)} size="large" color="cancel">
            {t('cancel')}
          </Button>
          <Button data-testid="user-add-submit" type="submit" size="large" disabled={loading}>
            {loading ? t('adding') || 'Adding...' : t('add_user')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddUserModal;
